/* ============================================================================
   Shared SEO config for the public site. Used by the React pages and by
   scripts/prerender.js, which writes the static HTML for every public URL.
   ========================================================================== */

export const SITE_URL = "https://teorikorkort.se";
export const APP_STORE_ID = "6777672930";
export const APP_STORE_URL =
  "https://apps.apple.com/se/app/teorik%C3%B6rkort/id6777672930";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.stefkolm.korkort";
export const INSTAGRAM_URL = "https://www.instagram.com/teorikorkort.se";
export const CONTACT_EMAIL = "support@teorikorkort.se";

// Open Graph / Twitter share image (1200×630).
export const SHARE_IMAGE = { url: `${SITE_URL}/images/og-image.jpg`, width: 1200, height: 630 };
export const LOGO = { url: `${SITE_URL}/images/icon.png`, width: 1024, height: 1024 };

export const LANGS = [
  { code: "sv", short: "SV", native: "Svenska" },
  { code: "en", short: "EN", native: "English" },
  { code: "uk", short: "UK", native: "Українська" },
  { code: "ru", short: "RU", native: "Русский" },
];
export const LANG_CODES = LANGS.map((l) => l.code);
export const DEFAULT_LANG = "sv";

export const OG_LOCALE = { sv: "sv_SE", en: "en_GB", uk: "uk_UA", ru: "ru_RU" };

/* Path of the landing page for a language, e.g. "/en/". */
export const langPath = (code) => `/${code}/`;
export const absUrl = (path) => `${SITE_URL}${path}`;

/* hreflang cluster shared by "/" (x-default) and the four language pages. */
export function landingAlternates() {
  return [
    ...LANG_CODES.map((code) => ({ hreflang: code, href: absUrl(langPath(code)) })),
    { hreflang: "x-default", href: absUrl("/") },
  ];
}

/* --- Structured data (JSON-LD) ---------------------------------------------
   Only facts that are visible on the pages: no ratings or reviews are marked
   up. Nodes are linked by @id so every page shares one Organization/WebSite. */

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export function organizationNode() {
  return {
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Teorikörkort",
    url: `${SITE_URL}/`,
    logo: { "@type": "ImageObject", ...LOGO },
    email: CONTACT_EMAIL,
    sameAs: [INSTAGRAM_URL],
  };
}

export function websiteNode() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: "Teorikörkort",
    inLanguage: LANG_CODES,
    publisher: { "@id": ORG_ID },
  };
}

export function webPageNode({ url, name, description, lang, aboutId }) {
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name,
    description,
    inLanguage: lang,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    ...(aboutId ? { about: { "@id": aboutId } } : {}),
  };
}

/* The app as described on a landing page. plans = [{ name, price }] in SEK,
   taken from the pricing section shown on the same page. */
export function mobileAppNode({ url, description, plans }) {
  return {
    "@type": "MobileApplication",
    "@id": `${url}#app`,
    name: "Teorikörkort",
    description,
    url,
    image: LOGO.url,
    applicationCategory: "EducationalApplication",
    operatingSystem: "iOS, Android",
    inLanguage: LANG_CODES, // the app itself is fully translated into all four
    downloadUrl: [APP_STORE_URL, PLAY_STORE_URL],
    sameAs: [APP_STORE_URL, PLAY_STORE_URL],
    publisher: { "@id": ORG_ID },
    offers: plans.map((p) => ({ "@type": "Offer", name: p.name, price: p.price, priceCurrency: "SEK" })),
  };
}

/* Breadcrumb trail; must match the breadcrumb shown on the page.
   items = [{ name, url }, …] from the home page down to the current page. */
export function breadcrumbNode(url, items) {
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export const jsonLdGraph =(nodes) => ({ "@context": "https://schema.org", "@graph": nodes });

/* Legacy URLs used "/?lang=xx". Returns the new path (keeping every other
   query parameter, e.g. utm_*) or null when no redirect is needed. */
export function legacyLangRedirect(pathname, search, hash = "") {
  if (pathname !== "/") return null;
  const params = new URLSearchParams(search);
  const lang = params.get("lang");
  if (!lang || !LANG_CODES.includes(lang)) return null;
  params.delete("lang");
  const rest = params.toString();
  return `${langPath(lang)}${rest ? `?${rest}` : ""}${hash}`;
}
